import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Toast from '../components/Toast';
import { registerOfflineToastNotifier } from './offlineToastRegistry';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 10000) => {
    const id = Date.now();
    const toast = { id, message, type, duration };

    setToasts(prev => [...prev, toast]);

    // Auto remove after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration + 500); // Add buffer for animation
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showSuccess = useCallback((message, duration = 10000) => {
    addToast(message, 'success', duration);
  }, [addToast]);

  const showError = useCallback((message, duration = 10000) => {
    addToast(message, 'error', duration);
  }, [addToast]);

  const showInfo = useCallback((message, duration = 10000) => {
    addToast(message, 'info', duration);
  }, [addToast]);

  // Let useLocalMutation's module-level offline-toast coalescer reach the toast
  // UI without pulling useToast() into that hook (it mounts above this provider).
  useEffect(() => {
    const notify = (message) => showInfo(message);
    registerOfflineToastNotifier(notify);
    // Compare-before-clear: only unregister if we're still the active notifier,
    // so a second provider's registration isn't clobbered on our unmount.
    return () => registerOfflineToastNotifier(null, notify);
  }, [showInfo]);

  return (
    <ToastContext.Provider value={{ addToast, showSuccess, showError, showInfo }}>
      {children}
      {/* Render toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};